import { FAMILY, SEED } from './data'
import { ARCHITECTURES, type ArchitectureId } from './architecture'
import { SHARES_OFF, SHARES_ON, type DemoState, type Flow } from './state'

/**
 * The state each scripted flow starts from.
 *
 * Shared by the sidebar's flow buttons and by the architecture switch, so that
 * re-telling the story under the other data model lands on exactly the same
 * setup rather than a half-configured group.
 */
const BASE: Partial<DemoState> = {
  picked: false,
  alerts: [SEED],
  unread: 0,
  kAlerts: [],
  breaches: [],
  kUnread: 0,
  kBlockCount: 0,
  mPush: null,
  blocked: null,
  devState: {},
  shareSheet: false,
  shareSheetKind: 'invite',
  aliases: {},
  aliasDraft: null,
  sharingPaused: false,
  memberShares: SHARES_OFF,
  groupNameDraft: null,
  memberKey: 'self',
  memberFeatures: { maliciousSite: true, scamCall: true, adTracker: true },
}

/**
 * Which flows actually diverge between the two data models.
 *
 * Buying the plan and inviting a member are pure account mechanics — they look
 * and behave identically whether or not the backend maps users to devices. The
 * architectures only part company once a device starts reporting threats, so
 * switching model on Flow 1 or 2 leaves the scenario exactly where it was
 * rather than replaying it for no visible reason.
 */
export const FLOW_DEPENDS_ON_ARCHITECTURE: Record<Flow, boolean> = {
  1: false,
  2: false,
  3: true,
  4: true,
}

export function flowPreset(flow: Flow, archId: ArchitectureId): Partial<DemoState> {
  const arch = ARCHITECTURES[archId]

  switch (flow) {
    case 1:
      return {
        ...BASE,
        flow: 1,
        m: 'connect',
        k: 'lock',
        kPush: 'none',
        joined: false,
        inviteTarget: false,
        invites: [],
        deviceAsked: false,
        deviceAccepted: false,
      }
    case 2:
      return {
        ...BASE,
        flow: 2,
        m: 'sub',
        k: 'lock',
        kPush: 'none',
        joined: false,
        inviteTarget: false,
        invites: [],
        deviceAsked: false,
        deviceAccepted: false,
      }
    case 3:
      return {
        ...BASE,
        flow: 3,
        /**
         * Where sharing is arranged differs by model. Zero knowledge keeps it
         * beside the group itself, under Group features, because pairing is a
         * concern of the group rather than of any member. The mapped model
         * hangs devices off the member who owns them, so it opens that member.
         */
        m: arch.exposesMemberDevices ? 'member' : 'sub',
        memberKey: arch.exposesMemberDevices ? 'vito' : 'self',
        k: 'home',
        kPush: 'none',
        joined: true,
        inviteTarget: true,
        invites: FAMILY,
        deviceAsked: false,
        deviceAccepted: false,
      }
    case 4:
      return {
        ...BASE,
        flow: 4,
        m: 'lock',
        k: 'home',
        kPush: 'none',
        joined: true,
        inviteTarget: true,
        invites: FAMILY,
        deviceAsked: true,
        deviceAccepted: true,
        memberShares: SHARES_ON,
      }
  }
}
